import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, photoUrl, uploadPhoto } from "@/lib/api";
import { ALL_COUNTRIES, COUNTRY_GROUPS, DEAL_BREAKER_OPTIONS } from "@/lib/countries";
import { useAuth } from "@/context/AuthContext";
import { Check, ArrowLeft, ArrowRight, Upload, X, Settings, Star } from "lucide-react";

// 10-step onboarding matching the Find Your Naseeb spec.
// Every step persists progress to the backend as a "draft" so users can resume.

const STEPS = [
  { key: "who", label: "Hvem er profilen for?" },
  { key: "basic", label: "Grundoplysninger" },
  { key: "languages", label: "Sprog" },
  { key: "intention", label: "Ægteskabsintention" },
  { key: "faith", label: "Tro & livsstil" },
  { key: "journey", label: "Din rejse" },
  { key: "career", label: "Uddannelse & karriere" },
  { key: "family", label: "Familie & fremtid" },
  { key: "photos", label: "Fotos" },
  { key: "voice", label: "Din stemme" },
  { key: "prefs", label: "Partnerpræferencer" },
  { key: "review", label: "Gennemgang" },
];

const BLANK = {
  profile_for: "myself",
  display_name: "",
  gender: "male",
  date_of_birth: "",
  city: "",
  country: "Danmark",
  nationality: "Pakistansk",
  marital_status: "never_married",
  languages: [],
  marriage_timeline: "",
  sect: "sunni",
  religious_practice: "practicing",
  prays_five_times: false,
  wears_hijab: null,
  keeps_beard: null,
  smokes: false,
  drinks_alcohol: false,
  halal_lifestyle: true,
  family_background: "",
  education: "",
  profession: "",
  industry: "",
  about_me: "",
  values: [],
  want_children: "",
  has_children: false,
  family_involvement: "",
  relocation: "",
  pref_age_min: 22,
  pref_age_max: 35,
  pref_countries: [],
  pref_sects: [],
  pref_marriage_timeline: "",
  pref_relocation: "",
  photos: [],
  main_photo_index: 0,
  wali_name: "",
  wali_email: "",
  wali_relation: "",
  origin_countries: [],
  pref_origin_countries: [],
  pref_open_to_world: false,
  deal_breakers: [],
  prompts: [],
  photo_captions: {},
  deen_journey_type: "",
  deen_journey_note: "",
  life_now: "",
  life_next: "",
};

const LANGUAGES_OPTIONS = ["Urdu", "English", "Punjabi", "Pashto", "Sindhi", "Balochi", "Dansk", "Svensk", "Norsk", "Tysk", "Arabisk", "Fransk"];
const COUNTRIES_OPTIONS = ALL_COUNTRIES;
const INTENTIONS = [
  ["ready", "Klar til ægteskab"],
  ["6m", "Inden for 6 måneder"],
  ["1y", "Inden for 1 år"],
  ["1_2y", "1–2 år"],
  ["exploring", "Udforsker seriøst"],
];
const CHILDREN_OPTS = [["yes","Ja"],["no","Nej"],["maybe","Måske"],["already_have","Har allerede"]];
const RELOC_OPTS = [["yes","Ja"],["maybe","Måske"],["no","Nej"]];
const FAM_OPTS = [["active","Aktivt involveret"],["supportive","Støttende"],["private","Privat"]];

export default function ProfileWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && typeof user === "object" && (user.role === "admin" || user.role === "moderator");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(BLANK);
  const [status, setStatus] = useState("draft");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/profile/me").then((r) => {
      if (r.data) {
        setForm({ ...BLANK, ...r.data });
        setStatus(r.data.status || "draft");
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => {
    const arr = f[k] || [];
    return { ...f, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
  });

  const validateStep = (idx) => {
    if (idx === 1) {
      const missing = [];
      if (!form.display_name?.trim()) missing.push("Fornavn");
      if (!form.date_of_birth) missing.push("Fødselsdato");
      if (!form.city?.trim()) missing.push("By");
      if (!form.country?.trim()) missing.push("Land");
      if (missing.length) return `Udfyld venligst: ${missing.join(", ")}`;
    }
    return "";
  };

  const saveDraft = async (patch = {}) => {
    setBusy(true); setErr("");
    try {
      await api.post("/profile", { ...form, ...patch, submit: false });
    } catch (e) { setErr(formatApiError(e)); throw e; }
    finally { setBusy(false); }
  };

  const submitProfile = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/profile", { ...form, submit: true });
      navigate("/matches");
    } catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };

  const next = async () => {
    const v = validateStep(step);
    if (v) { setErr(v); return; }
    // Only persist to backend once Basic fields (Step 2 / index 1) are filled — backend requires them.
    const hasBasics = form.display_name?.trim() && form.gender && form.date_of_birth;
    try {
      if (hasBasics) await saveDraft();
      if (step < STEPS.length - 1) setStep(step + 1);
    } catch {}
  };
  const back = () => step > 0 && setStep(step - 1);

  if (!loaded) return <div className="max-w-3xl mx-auto px-4 py-20 text-brown-500">Indlæser…</div>;

  if (isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center animate-fade-up" data-testid="profile-admin-shortcut">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gold-500/15 text-gold-600 mx-auto"><Settings className="h-7 w-7" /></div>
        <div className="eyebrow mt-6">Admin-konto</div>
        <h1 className="mt-2 font-serif text-4xl text-olive-700">Admin bruger ikke en profil</h1>
        <p className="mt-4 text-brown-600">Din rolle har fuld adgang til platformen.</p>
        <Link to="/admin" className="btn-primary mt-8 inline-flex"><Settings className="h-4 w-4" /> Gå til kontrolpanel</Link>
      </div>
    );
  }

  if (status === "pending") return <Pending />;

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8" data-testid="profile-wizard-page">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-brown-500">
          <span data-testid="step-label">{step + 1}. {STEPS[step].label}</span>
          <span>Trin {step + 1} af {STEPS.length}</span>
        </div>
        <div className="mt-2 h-1.5 bg-sand-200 rounded-full overflow-hidden">
          <div className="h-full bg-olive-500 transition-all" style={{ width: `${progress}%` }} data-testid="progress-bar" />
        </div>
      </div>

      <div className="card-soft p-6 sm:p-8" data-testid={`step-${STEPS[step].key}`}>
        {step === 0 && <StepWho form={form} set={set} />}
        {step === 1 && <StepBasic form={form} set={set} />}
        {step === 2 && <StepLanguages form={form} toggleArr={toggleArr} />}
        {step === 3 && <StepIntention form={form} set={set} />}
        {step === 4 && <StepFaith form={form} set={set} />}
        {step === 5 && <StepJourney form={form} set={set} />}
        {step === 6 && <StepCareer form={form} set={set} />}
        {step === 7 && <StepFamily form={form} set={set} toggleArr={toggleArr} />}
        {step === 8 && <StepPhotos form={form} set={set} setErr={setErr} />}
        {step === 9 && <StepVoice form={form} set={set} />}
        {step === 10 && <StepPrefs form={form} set={set} toggleArr={toggleArr} />}
        {step === 11 && <StepReview form={form} />}
      </div>

      {err && <div className="mt-3 text-destructive text-sm" data-testid="wizard-error">{err}</div>}

      <div className="mt-6 flex items-center justify-between gap-3">
        <button onClick={back} disabled={step === 0} className="btn-secondary py-2.5 disabled:opacity-40" data-testid="wizard-back">
          <ArrowLeft className="h-4 w-4" /> Tilbage
        </button>
        {step < STEPS.length - 1 ? (
          <button onClick={next} disabled={busy} className="btn-primary flex-1 sm:flex-none" data-testid="wizard-next">
            Næste <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={submitProfile} disabled={busy} className="btn-primary flex-1 sm:flex-none" data-testid="wizard-submit">
            {busy ? "Sender…" : "Send til godkendelse"}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Step components ----------
function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-sm text-brown-600">{label}</span>
      {hint && <span className="text-xs text-brown-500 block mb-1">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
function OptionCard({ selected, onClick, label, sub, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full text-left px-4 py-3.5 rounded-xl border transition ${selected ? "border-olive-500 bg-olive-50 ring-2 ring-olive-500/20" : "border-sand-300 hover:border-olive-500/40 bg-white"}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-medium text-olive-700">{label}</div>
          {sub && <div className="text-xs text-brown-500 mt-0.5">{sub}</div>}
        </div>
        {selected && <Check className="h-5 w-5 text-olive-600 shrink-0" />}
      </div>
    </button>
  );
}
function Chip({ selected, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-sm border transition ${selected ? "bg-olive-500 text-white border-olive-500" : "bg-white border-sand-300 text-brown-600 hover:border-olive-500/40"}`}>
      {children}
    </button>
  );
}

function StepWho({ form, set }) {
  const opts = [
    ["myself", "Mig selv", "Jeg søger min egen partner"],
    ["son", "Min søn", "Jeg opretter for min søn"],
    ["daughter", "Min datter", "Jeg opretter for min datter"],
    ["sibling", "Min bror/søster", "Jeg opretter for et søskende"],
    ["family", "Andet familiemedlem", "Fx onkel, tante, kusine"],
  ];
  return (
    <div>
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Hvem er profilen for?</h2>
      <p className="text-sm text-brown-500 mb-5">I mange familier hjælpes vi ad. Vælg hvem denne profil repræsenterer.</p>
      <div className="space-y-2">
        {opts.map(([v, l, s]) => (
          <OptionCard key={v} selected={form.profile_for === v} onClick={() => set("profile_for", v)} label={l} sub={s} testid={`who-${v}`} />
        ))}
      </div>
    </div>
  );
}

function StepBasic({ form, set }) {
  return (
    <div className="space-y-4">
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Grundoplysninger</h2>
      <Field label="Fornavn"><input className="field" value={form.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="basic-name" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Fødselsdato"><input type="date" className="field" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="basic-dob" /></Field>
        <Field label="Køn">
          <select className="field" value={form.gender} onChange={(e) => set("gender", e.target.value)} data-testid="basic-gender">
            <option value="male">Mand</option><option value="female">Kvinde</option>
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Land"><select className="field" value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="basic-country">{COUNTRIES_OPTIONS.map(c => <option key={c}>{c}</option>)}</select></Field>
        <Field label="By"><input className="field" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="basic-city" placeholder="København, Lahore, ..." /></Field>
      </div>
      <Field label="Baggrund/nationalitet"><input className="field" value={form.nationality} onChange={(e) => set("nationality", e.target.value)} data-testid="basic-nationality" placeholder="Pakistansk, Dansk-Pakistansk, ..." /></Field>
      <Field label="Oprindelseslande (kan vælge flere — heritage)">
        <CountryPicker
          value={form.origin_countries || []}
          onChange={(v) => set("origin_countries", v)}
          testid="origin-countries"
        />
      </Field>
      <Field label="Civilstand">
        <select className="field" value={form.marital_status} onChange={(e) => set("marital_status", e.target.value)} data-testid="basic-marital">
          <option value="never_married">Aldrig gift</option><option value="divorced">Skilt</option><option value="widowed">Enke/enkemand</option>
        </select>
      </Field>
    </div>
  );
}

function StepLanguages({ form, toggleArr }) {
  return (
    <div>
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Hvilke sprog taler du?</h2>
      <p className="text-sm text-brown-500 mb-5">Vælg alle der passer — hjælper os med at finde kulturelle matches.</p>
      <div className="flex flex-wrap gap-2">
        {LANGUAGES_OPTIONS.map((l) => (
          <Chip key={l} selected={(form.languages || []).includes(l)} onClick={() => toggleArr("languages", l)} testid={`lang-${l}`}>{l}</Chip>
        ))}
      </div>
    </div>
  );
}

function StepIntention({ form, set }) {
  return (
    <div>
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Hvornår er du klar til ægteskab?</h2>
      <p className="text-sm text-brown-500 mb-5">Ærlighed hjælper alle. Der er ingen forkerte svar.</p>
      <div className="space-y-2">
        {INTENTIONS.map(([v, l]) => (
          <OptionCard key={v} selected={form.marriage_timeline === v} onClick={() => set("marriage_timeline", v)} label={l} testid={`intent-${v}`} />
        ))}
      </div>
    </div>
  );
}

function StepFaith({ form, set }) {
  return (
    <div className="space-y-4">
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Tro & livsstil</h2>
      <p className="text-sm text-brown-500 -mt-1">Del kun det du er komfortabel med.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Retning">
          <select className="field" value={form.sect} onChange={(e) => set("sect", e.target.value)} data-testid="faith-sect">
            <option value="sunni">Sunni</option><option value="shia">Shia</option><option value="other">Andet</option><option value="prefer_not_to_say">Foretrækker ikke at sige</option>
          </select>
        </Field>
        <Field label="Praksis">
          <select className="field" value={form.religious_practice} onChange={(e) => set("religious_practice", e.target.value)} data-testid="faith-practice">
            <option value="very_practicing">Meget praktiserende</option><option value="practicing">Praktiserende</option><option value="moderate">Moderat</option><option value="learning">På rejse</option>
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-2">
        <Toggle label="Beder 5 gange dagligt" checked={form.prays_five_times} onChange={(v) => set("prays_five_times", v)} testid="faith-pray" />
        <Toggle label="Halal livsstil" checked={form.halal_lifestyle} onChange={(v) => set("halal_lifestyle", v)} testid="faith-halal" />
        <Toggle label="Ryger" checked={form.smokes} onChange={(v) => set("smokes", v)} testid="faith-smoke" />
        <Toggle label="Drikker alkohol" checked={form.drinks_alcohol} onChange={(v) => set("drinks_alcohol", v)} testid="faith-alcohol" />
        {form.gender === "female" && <Toggle label="Bærer hijab" checked={!!form.wears_hijab} onChange={(v) => set("wears_hijab", v)} testid="faith-hijab" />}
        {form.gender === "male" && <Toggle label="Har skæg" checked={!!form.keeps_beard} onChange={(v) => set("keeps_beard", v)} testid="faith-beard" />}
      </div>
    </div>
  );
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className={`flex items-center justify-between px-4 py-3 rounded-xl border transition ${checked ? "border-olive-500 bg-olive-50" : "border-sand-300 bg-white"}`} data-testid={testid}>
      <span className="text-brown-700 text-sm">{label}</span>
      <span className={`h-6 w-11 rounded-full relative transition ${checked ? "bg-olive-500" : "bg-sand-300"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${checked ? "left-5" : "left-0.5"}`} />
      </span>
    </button>
  );
}

function StepCareer({ form, set }) {
  return (
    <div className="space-y-4">
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Uddannelse & karriere</h2>
      <Field label="Uddannelse"><input className="field" value={form.education} onChange={(e) => set("education", e.target.value)} placeholder="Fx Kandidat i medicin" data-testid="career-education" /></Field>
      <Field label="Profession"><input className="field" value={form.profession} onChange={(e) => set("profession", e.target.value)} placeholder="Fx Farmaceut" data-testid="career-profession" /></Field>
      <Field label="Branche"><input className="field" value={form.industry} onChange={(e) => set("industry", e.target.value)} placeholder="Fx Sundhed" data-testid="career-industry" /></Field>
    </div>
  );
}

function StepFamily({ form, set, toggleArr }) {
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Familie & fremtid</h2>
      <Field label="Familiebaggrund (kort)"><textarea className="field min-h-[80px]" value={form.family_background} onChange={(e) => set("family_background", e.target.value)} data-testid="family-bg" placeholder="Fx: Vi er en tætknyttet familie med rod i Punjab..." /></Field>

      <Field label="Ønsker du børn?">
        <div className="flex flex-wrap gap-2">
          {CHILDREN_OPTS.map(([v,l]) => <Chip key={v} selected={form.want_children===v} onClick={() => set("want_children", v)} testid={`fam-children-${v}`}>{l}</Chip>)}
        </div>
      </Field>

      <Field label="Familieinvolvering">
        <div className="flex flex-wrap gap-2">
          {FAM_OPTS.map(([v,l]) => <Chip key={v} selected={form.family_involvement===v} onClick={() => set("family_involvement", v)} testid={`fam-involve-${v}`}>{l}</Chip>)}
        </div>
      </Field>

      <Field label="Åben for at flytte?">
        <div className="flex flex-wrap gap-2">
          {RELOC_OPTS.map(([v,l]) => <Chip key={v} selected={form.relocation===v} onClick={() => set("relocation", v)} testid={`fam-relocate-${v}`}>{l}</Chip>)}
        </div>
      </Field>

      <Field label="Værdier der beskriver dig" hint="Vælg 3-6">
        <div className="flex flex-wrap gap-2">
          {["Familieorienteret","Venlig","Tålmodig","Ambitiøs","Beskeden","Sunnah-bevidst","Iværksætter","Rejsende","Uddannelses-fokuseret","Stille-natur"].map((v) => (
            <Chip key={v} selected={(form.values||[]).includes(v)} onClick={() => toggleArr("values", v)} testid={`fam-value-${v}`}>{v}</Chip>
          ))}
        </div>
      </Field>

      <Field label="Om dig"><textarea className="field min-h-[100px]" value={form.about_me} onChange={(e) => set("about_me", e.target.value)} placeholder="Fortæl kort om dig selv — hvad du søger, hvad du sætter pris på..." data-testid="family-about" /></Field>
    </div>
  );
}

function StepPhotos({ form, set, setErr }) {
  const [busy, setBusy] = React.useState(false);
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - (form.photos || []).length);
    if (!files.length) return;
    setBusy(true); setErr("");
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > 8 * 1024 * 1024) { setErr("Foto skal være under 8 MB"); continue; }
        try {
          const { path } = await uploadPhoto(file);
          uploaded.push(path);
        } catch (err) {
          setErr(formatApiError(err));
        }
      }
      if (uploaded.length) set("photos", [...(form.photos || []), ...uploaded]);
    } finally { setBusy(false); }
    e.target.value = "";
  };
  const remove = (i) => {
    const photos = form.photos.filter((_, idx) => idx !== i);
    // Rebuild captions with new indices
    const oldCaps = form.photo_captions || {};
    const newCaps = {};
    photos.forEach((_, ni) => {
      const oldIdx = ni < i ? ni : ni + 1;
      if (oldCaps[String(oldIdx)]) newCaps[String(ni)] = oldCaps[String(oldIdx)];
    });
    set("photos", photos);
    set("photo_captions", newCaps);
  };
  const setCaption = (i, v) => {
    const caps = { ...(form.photo_captions || {}) };
    if (v) caps[String(i)] = v.slice(0, 60); else delete caps[String(i)];
    set("photo_captions", caps);
  };
  const move = (from, to) => {
    if (from === to) return;
    const photos = [...form.photos];
    const [item] = photos.splice(from, 1);
    photos.splice(to, 0, item);
    // Rebuild captions map with new indices
    const oldCaps = form.photo_captions || {};
    const idxMap = {}; // oldIdx -> newIdx
    form.photos.forEach((_, oldIdx) => {
      let newIdx = oldIdx;
      if (oldIdx === from) newIdx = to;
      else if (from < to && oldIdx > from && oldIdx <= to) newIdx = oldIdx - 1;
      else if (from > to && oldIdx >= to && oldIdx < from) newIdx = oldIdx + 1;
      idxMap[oldIdx] = newIdx;
    });
    const newCaps = {};
    Object.entries(oldCaps).forEach(([k, v]) => {
      const ni = idxMap[parseInt(k)];
      if (ni != null) newCaps[String(ni)] = v;
    });
    set("photos", photos);
    set("photo_captions", newCaps);
  };
  return (
    <div>
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Fotos</h2>
      <p className="text-sm text-brown-500 mb-4">Upload 1-6 realistiske fotos. Første foto er hovedbilledet. Træk for at ændre rækkefølge.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {(form.photos || []).map((p, i) => (
          <div key={p + i} className="space-y-1.5" data-testid={`photo-slot-${i}`}>
            <div className="relative aspect-square rounded-xl overflow-hidden border-2 border-sand-300 group">
              <img loading="lazy" decoding="async" src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
              {i === 0 && <div className="absolute top-1 left-1 bg-gold-500 text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Star className="h-2.5 w-2.5" /> Hoved</div>}
              <button onClick={() => remove(i)} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 focus:opacity-100" data-testid={`remove-photo-${i}`} aria-label="Fjern foto"><X className="h-3 w-3" /></button>
              <div className="absolute bottom-1 inset-x-1 flex justify-between opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                <button onClick={() => move(i, Math.max(0, i - 1))} disabled={i === 0} className="bg-black/60 text-white rounded-full p-1 disabled:opacity-30" aria-label="Flyt venstre" data-testid={`move-left-${i}`}><ArrowLeft className="h-3 w-3" /></button>
                <button onClick={() => move(i, Math.min((form.photos || []).length - 1, i + 1))} disabled={i === (form.photos || []).length - 1} className="bg-black/60 text-white rounded-full p-1 disabled:opacity-30" aria-label="Flyt højre" data-testid={`move-right-${i}`}><ArrowRight className="h-3 w-3" /></button>
              </div>
            </div>
            <input
              type="text"
              maxLength={60}
              value={(form.photo_captions || {})[String(i)] || ""}
              onChange={(e) => setCaption(i, e.target.value)}
              placeholder="Beskrivelse (fx Eid 2024)"
              className="field w-full text-xs py-1.5"
              data-testid={`caption-${i}`}
            />
          </div>
        ))}
        {(form.photos || []).length < 6 && (
          <label className={`aspect-square rounded-xl border-2 border-dashed border-sand-400 flex flex-col items-center justify-center text-brown-500 cursor-pointer hover:border-olive-500 hover:text-olive-600 ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="upload-photo">
            <Upload className="h-5 w-5" /><span className="text-xs mt-1">{busy ? "Uploader…" : "Upload"}</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} disabled={busy} />
          </label>
        )}
      </div>
    </div>
  );
}

function StepPrefs({ form, set, toggleArr }) {
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Partnerpræferencer</h2>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Alder fra"><input type="number" min="18" max="80" className="field" value={form.pref_age_min} onChange={(e) => set("pref_age_min", parseInt(e.target.value || "18"))} data-testid="pref-age-min" /></Field>
        <Field label="Alder til"><input type="number" min="18" max="80" className="field" value={form.pref_age_max} onChange={(e) => set("pref_age_max", parseInt(e.target.value || "60"))} data-testid="pref-age-max" /></Field>
      </div>

      <Field label="Bopælslande jeg er åben overfor" hint="Efterlad tom + slå 'resten af verden' til hvis du vil søge bredt">
        <label className="mb-3 inline-flex items-center gap-2 text-sm text-brown-600 bg-olive-50 border border-olive-500/20 rounded-full px-3 py-1.5 cursor-pointer" data-testid="pref-open-to-world">
          <input type="checkbox" checked={!!form.pref_open_to_world} onChange={(e) => set("pref_open_to_world", e.target.checked)} />
          Åben for hele verden
        </label>
        {!form.pref_open_to_world && (
          <CountryPicker
            value={form.pref_countries || []}
            onChange={(v) => set("pref_countries", v)}
            testid="pref-countries"
          />
        )}
      </Field>

      <Field label="Ønsket baggrund/oprindelse (valgfrit)" hint="Kun kandidater hvis heritage matcher">
        <CountryPicker
          value={form.pref_origin_countries || []}
          onChange={(v) => set("pref_origin_countries", v)}
          testid="pref-origin-countries"
        />
      </Field>

      <Field label="Ønsket ægteskabsintention">
        <div className="flex flex-wrap gap-2">
          {INTENTIONS.map(([v,l]) => <Chip key={v} selected={form.pref_marriage_timeline===v} onClick={() => set("pref_marriage_timeline", v)} testid={`pref-intent-${v}`}>{l}</Chip>)}
        </div>
      </Field>
      <Field label="Åben for partner der flytter?">
        <div className="flex flex-wrap gap-2">
          {RELOC_OPTS.map(([v,l]) => <Chip key={v} selected={form.pref_relocation===v} onClick={() => set("pref_relocation", v)} testid={`pref-reloc-${v}`}>{l}</Chip>)}
        </div>
      </Field>

      <Field label="Deal-breakers" hint="Absolutte krav — kandidater der ikke matcher, vises ikke i din feed. Vælg med omtanke.">
        <div className="grid gap-2 sm:grid-cols-2">
          {DEAL_BREAKER_OPTIONS.map((opt) => {
            const active = (form.deal_breakers || []).includes(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => toggleArr("deal_breakers", opt.key)}
                className={`text-left px-3 py-2 rounded-xl border transition ${active ? "bg-destructive/10 border-destructive/40 text-destructive" : "bg-white border-sand-300 text-brown-700 hover:border-olive-500/40"}`}
                data-testid={`deal-breaker-${opt.key}`}
              >
                <div className="text-sm font-medium">{active ? "✓ " : ""}{opt.label}</div>
                <div className="text-[11px] text-brown-500 mt-0.5">{opt.hint}</div>
              </button>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

// Country picker with grouped optgroups + chip display for selected + search
function CountryPicker({ value = [], onChange, testid }) {
  const [q, setQ] = React.useState("");
  const filtered = React.useMemo(() => {
    if (!q) return COUNTRY_GROUPS;
    const needle = q.toLowerCase();
    return COUNTRY_GROUPS.map((g) => ({
      label: g.label,
      countries: g.countries.filter((c) => c.toLowerCase().includes(needle)),
    })).filter((g) => g.countries.length);
  }, [q]);
  const toggle = (c) => {
    if (value.includes(c)) onChange(value.filter((x) => x !== c));
    else onChange([...value, c]);
  };
  return (
    <div data-testid={testid}>
      {value.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {value.map((c) => (
            <button key={c} type="button" onClick={() => toggle(c)} className="inline-flex items-center gap-1 text-xs bg-olive-500 text-white rounded-full pl-3 pr-2 py-1 hover:bg-olive-600" data-testid={`${testid}-chip-${c}`}>
              {c} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
      <input
        type="text"
        placeholder="Søg lande…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="field w-full mb-2 text-sm"
        data-testid={`${testid}-search`}
      />
      <div className="max-h-60 overflow-y-auto rounded-xl border border-sand-300 bg-white">
        {filtered.map((g) => (
          <div key={g.label} className="border-b border-sand-200 last:border-0">
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-brown-400 bg-sand-100">{g.label}</div>
            <div className="p-2 flex flex-wrap gap-1.5">
              {g.countries.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggle(c)}
                  className={`text-xs px-3 py-1 rounded-full border ${value.includes(c) ? "bg-olive-500 text-white border-olive-500" : "bg-white text-brown-600 border-sand-300 hover:border-olive-500/40"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepReview({ form }) {
  const row = (l, v) => (
    <div className="flex justify-between py-2 border-b border-sand-200 last:border-0 text-sm">
      <span className="text-brown-500">{l}</span>
      <span className="text-olive-700 font-medium text-right max-w-[65%] truncate">{v || "—"}</span>
    </div>
  );
  return (
    <div>
      <h2 className="font-serif text-2xl text-olive-700 mb-1">Gennemgang</h2>
      <p className="text-sm text-brown-500 mb-5">Kig grundigt igennem. Vi gennemgår hver profil manuelt før den bliver aktiv.</p>
      <div className="rounded-xl bg-sand-100 p-4">
        {row("Profil for", form.profile_for)}
        {row("Navn", form.display_name)}
        {row("Alder/køn", `${form.date_of_birth} · ${form.gender === "male" ? "Mand" : "Kvinde"}`)}
        {row("Sted", `${form.city}, ${form.country}`)}
        {row("Baggrund", form.nationality)}
        {row("Sprog", (form.languages || []).join(", "))}
        {row("Intention", INTENTIONS.find((i) => i[0] === form.marriage_timeline)?.[1])}
        {row("Retning/praksis", `${form.sect} · ${form.religious_practice}`)}
        {row("Uddannelse", form.education)}
        {row("Profession", form.profession)}
        {row("Fotos", `${(form.photos || []).length} uploadet`)}
        {row("Alder-præference", `${form.pref_age_min}–${form.pref_age_max}`)}
        {row("Oprindelse", (form.origin_countries || []).join(", "))}
        {row("Åben for lande", form.pref_open_to_world ? "Hele verden" : (form.pref_countries || []).join(", ") || "—")}
        {row("Ønsket baggrund", (form.pref_origin_countries || []).join(", "))}
        {row("Deal-breakers", (form.deal_breakers || []).length ? `${(form.deal_breakers || []).length} valgt` : "Ingen")}
      </div>
      <div className="mt-5 rounded-xl bg-olive-50 border border-olive-500/20 p-4 text-sm text-brown-700">
        <div className="font-serif text-olive-700 text-base mb-1">Efter du sender ind</div>
        Vores team gennemgår din profil manuelt inden 24-48 timer. Du får en email når den bliver aktiv.
      </div>
    </div>
  );
}

function Pending() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="profile-pending">
      <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gold-500/15 text-gold-600 mx-auto"><Star className="h-7 w-7" /></div>
      <h1 className="mt-5 font-serif text-3xl text-olive-700">Din profil gennemgås</h1>
      <p className="mt-3 text-brown-600">Vi kontakter dig via email inden 24-48 timer.</p>
    </div>
  );
}

// ---------- Step: Din rejse (Deen journey + Life timeline) ----------
const DEEN_OPTIONS = [
  ["born_practicing", "Født muslim — praktiserende hele livet"],
  ["born_returning",  "Født muslim — genfundet praksis"],
  ["revert",          "Revert (konvertit)"],
  ["growing",         "Praktiserer gradvist"],
  ["seeking",         "Søgende / lærende"],
];

function StepJourney({ form, set }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-serif text-2xl text-olive-700 mb-1">Din rejse</h2>
        <p className="text-sm text-brown-500">Alvorlige mennesker vil vide hvor du er, og hvor du er på vej hen. To korte spørgsmål.</p>
      </div>

      <Field label="Din deen-rejse" hint="Hvordan har din vej med Islam set ud?">
        <div className="flex flex-wrap gap-2">
          {DEEN_OPTIONS.map(([v, l]) => (
            <Chip key={v} selected={form.deen_journey_type === v} onClick={() => set("deen_journey_type", v)} testid={`deen-${v}`}>{l}</Chip>
          ))}
        </div>
      </Field>

      <Field label="Fortæl kort om din rejse (valgfrit)">
        <textarea rows={3} maxLength={200} className="field w-full" value={form.deen_journey_note || ""} onChange={(e) => set("deen_journey_note", e.target.value.slice(0, 200))} placeholder="Fx: Jeg begyndte at bede regelmæssigt for 3 år siden efter en umrah — det ændrede alt for mig." data-testid="deen-note" />
        <div className="text-right text-xs text-brown-400">{(form.deen_journey_note || "").length}/200</div>
      </Field>

      <div className="pt-4 border-t border-sand-300">
        <h3 className="font-serif text-lg text-olive-700">Livs-tidslinje</h3>
        <p className="text-sm text-brown-500 mb-3">Hvor er du nu, og hvor er du på vej hen?</p>
        <Field label="Lige nu">
          <input maxLength={120} className="field w-full" value={form.life_now || ""} onChange={(e) => set("life_now", e.target.value.slice(0, 120))} placeholder="Fx: MA-studerende i medicin, arbejder som praktikant" data-testid="life-now" />
        </Field>
        <Field label="Om 12 måneder">
          <input maxLength={120} className="field w-full" value={form.life_next || ""} onChange={(e) => set("life_next", e.target.value.slice(0, 120))} placeholder="Fx: Færdig med studie, søger fast stilling" data-testid="life-next" />
        </Field>
      </div>
    </div>
  );
}

// ---------- Step: Din stemme (Hinge-style prompts) ----------
function StepVoice({ form, set }) {
  const [available, setAvailable] = React.useState([]);
  React.useEffect(() => {
    import("@/lib/api").then(({ api }) => api.get("/profile/prompts").then((r) => setAvailable(r.data || [])).catch(() => setAvailable([])));
  }, []);
  const answers = form.prompts || [];
  const setAnswer = (idx, patch) => {
    const next = [...answers];
    next[idx] = { ...(next[idx] || {}), ...patch };
    set("prompts", next.filter((x) => x && (x.key || x.answer)));
  };
  const remove = (idx) => { const next = [...answers]; next.splice(idx, 1); set("prompts", next); };
  const add = () => { if (answers.length < 5) set("prompts", [...answers, { key: "", answer: "" }]); };
  const usedKeys = new Set(answers.map((a) => a.key).filter(Boolean));

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-serif text-2xl text-olive-700 mb-1">Din stemme</h2>
        <p className="text-sm text-brown-500">Vælg 3-5 prompts og svar kort. Personlighed frem for tomme "om mig"-tekster.</p>
      </div>

      {answers.map((a, idx) => (
        <div key={idx} className="card-soft p-4 relative">
          <button onClick={() => remove(idx)} className="absolute top-2 right-2 text-brown-400 hover:text-destructive" data-testid={`prompt-remove-${idx}`} aria-label="Fjern"><X className="h-4 w-4" /></button>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-brown-500">Prompt</span>
            <select className="field w-full mt-1" value={a.key || ""} onChange={(e) => setAnswer(idx, { key: e.target.value })} data-testid={`prompt-select-${idx}`}>
              <option value="">Vælg et spørgsmål…</option>
              {available.filter((p) => p.key === a.key || !usedKeys.has(p.key)).map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </label>
          <label className="block mt-3">
            <span className="text-xs uppercase tracking-widest text-brown-500">Dit svar</span>
            <textarea rows={2} maxLength={150} className="field w-full mt-1" value={a.answer || ""} onChange={(e) => setAnswer(idx, { answer: e.target.value.slice(0, 150) })} placeholder="Skriv kort — max 150 tegn" data-testid={`prompt-answer-${idx}`} />
            <div className="text-right text-xs text-brown-400">{(a.answer || "").length}/150</div>
          </label>
        </div>
      ))}

      {answers.length < 5 && (
        <button onClick={add} className="btn-secondary w-full" data-testid="prompt-add">+ Tilføj prompt ({answers.length}/5)</button>
      )}
    </div>
  );
}

